import { GraduationCap } from "lucide-react"

import { MASTERCLASS_COVER_ASPECT_CLASS } from "@/lib/masterclass-cover"
import { cn } from "@/lib/utils"

/**
 * OKS-56 / OKS-54 — Shared deterministic colour swatch + `GraduationCap`
 * glyph for masterclass cards and Show headers.
 *
 * Extracted from the OKS-54 `masterclass-card.tsx` 6-tone palette so
 * both the brand-side card and the creator-side card consume one
 * source. Keeping the swatch deterministic-from-id means a given
 * masterclass renders the same colour across brand walks, creator
 * lists, and CTPO manual checks — stable visual identity until OKS-55+
 * ships real cover-image uploads (PRD §1 + TRD KD-7).
 *
 * # Palette
 *
 * 6 Tailwind tones tuned for AA contrast against the white
 * `GraduationCap` icon. Anchored on `Math.abs(id) % palette.length` so
 * negative-id pathologies (e.g. test fixtures with `id: -1` build_stubbed
 * rows) still map cleanly.
 *
 * # Sizes
 *
 * - `"card"`   — full-width 3:2 banner (shared
 *                `MASTERCLASS_COVER_ASPECT_CLASS`) — used on the brand-side
 *                Index card AND the creator-side Learn list card (both
 *                banners since OKS-836).
 * - `"thumb"`  — 56px (`h-14 w-14`) square — used on compact non-banner
 *                surfaces (e.g. the completed-masterclasses item). Renders
 *                inline rather than as a top banner.
 */
const SWATCH_PALETTE = [
  "bg-rose-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-sky-500",
  "bg-indigo-500",
  "bg-fuchsia-500",
] as const

/**
 * Public helper — useful for downstream specs/components that need to
 * assert the colour mapping without re-importing the palette literal.
 */
export function swatchClassFor(id: number): string {
  return SWATCH_PALETTE[Math.abs(id) % SWATCH_PALETTE.length]
}

export type MasterclassSwatchSize = "card" | "thumb"

export interface MasterclassSwatchProps {
  /**
   * Masterclass id (or any stable integer). The palette index is
   * `Math.abs(id) % 6`. For unsaved/preview rows pass `0` (deterministic
   * fallback — still renders the first palette tone, not a flash).
   */
  id: number
  size?: MasterclassSwatchSize
  /** Optional override classes (e.g. rounded corners on the thumb variant). */
  className?: string
  /** Optional data-testid hook — kept fully optional so existing OKS-54 callers don't break. */
  "data-testid"?: string
}

/**
 * Deterministic visual identity for a masterclass — solid colour
 * background + centered white `GraduationCap` glyph.
 *
 * `aria-hidden` on the wrapper: the icon carries no semantic meaning
 * beyond visual decoration; the surrounding title + topic chip + status
 * pill provide the actual screen-reader content.
 */
export function MasterclassSwatch({
  id,
  size = "card",
  className,
  "data-testid": testId,
}: MasterclassSwatchProps) {
  // OKS-768 — the `card` swatch adopts the masterclass banner ratio (3:2)
  // so the empty/fallback state shares proportions with the framed cover
  // (framed==shown). `thumb` stays a 56px square.
  const sizeClass =
    size === "card"
      ? `${MASTERCLASS_COVER_ASPECT_CLASS} w-full`
      : "h-14 w-14 rounded-md"

  return (
    <div
      className={cn(
        "flex items-center justify-center",
        sizeClass,
        swatchClassFor(id),
        className,
      )}
      aria-hidden="true"
      data-testid={testId}
    >
      <GraduationCap
        className={cn("text-white/95", size === "card" ? "size-12" : "size-7")}
      />
    </div>
  )
}
